import { ExternalLink } from "lucide-mui";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useQuery } from "@tanstack/react-query";
import { keys } from "../../../api/queryKeys.ts";
import { api } from "../../../api/client.ts";
import type { RegulatoryFilingListItem } from "../../../api/client.ts";
import { DetailGridItem } from "../../../components/DetailRow.tsx";
import EmptyState from "../../../components/EmptyState.tsx";
import FilterBar from "../../../components/filters/FilterBar.tsx";
import type { FilterSpec } from "../../../components/filters/specs.ts";
import { useFilters, useLocalFilterState } from "../../../components/filters/useFilters.ts";
import Panel from "../../../components/Panel.tsx";
import FormattedDate from "../../../components/FormattedDate.tsx";
import StatusChip from "../../../components/StatusChip.tsx";
import { SourceDataBlock } from "../../../components/detail/SourceDataCard.tsx";
import ExpandableRow from "../../../components/table/ExpandableRow.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";

/** The filing's raw source record. Not in the list response, so it is fetched
 * when a row opens (the expanded content unmounts on close). */
function FilingSourceData({ filingId }: { filingId: string }) {
  const { data } = useQuery({
    queryKey: keys.regulatoryFiling.detail(filingId),
    queryFn: () => api.getRegulatoryFiling(filingId),
  });
  return <SourceDataBlock data={data?.source_data} />;
}

function RegulatoryFilingRow({ filing }: { filing: RegulatoryFilingListItem }) {
  return (
    <ExpandableRow
      colSpan={7}
      cells={
        <>
          <TableCell>
            {filing.source_url ? (
              <Link
                href={filing.source_url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
              >
                {filing.title} <ExternalLink fontSize="inherit" />
              </Link>
            ) : (
              filing.title
            )}
          </TableCell>
          <TableCell>{filing.source}</TableCell>
          <TableCell>
            <StatusChip
              kind="regulatory_filing"
              value={filing.status}
              label={filing.status_raw ?? undefined}
              variant={filing.is_active ? "filled" : "outlined"}
            />
          </TableCell>
          <TableCell>
            <FormattedDate value={filing.filed_at} />
          </TableCell>
          <TableCell>
            <FormattedDate value={filing.hearing_deadline} />
          </TableCell>
          <TableCell>{filing.capacity_mw ?? "-"}</TableCell>
        </>
      }
    >
      <Grid container spacing={2}>
        <DetailGridItem label="Applicant (raw)" value={filing.applicant_name} />
        <DetailGridItem label="Filing type" value={filing.filing_type} />
        <DetailGridItem label="Subtype" value={filing.filing_subtype} />
        <DetailGridItem label="Region" value={filing.region} />
        <DetailGridItem label="Municipality" value={filing.subregion} />
        <DetailGridItem
          label="Estimated production (GWh)"
          value={filing.estimated_production_gwh != null ? String(filing.estimated_production_gwh) : null}
        />
        <DetailGridItem
          label="Decision date"
          value={filing.decision_at && <FormattedDate value={filing.decision_at} />}
        />
        <DetailGridItem label="Match method" value={filing.match_method} />
      </Grid>
      <FilingSourceData filingId={filing.id} />
    </ExpandableRow>
  );
}

// The same filters as Companies' "Regulatory filing" / "Filing status" pills.
const FILTER_SPECS: FilterSpec[] = [
  { kind: "text", key: "reg_kw", label: "Regulatory filing", param: "reg_kw", pinned: true, placeholder: "Filing mentions…" },
  {
    kind: "options",
    key: "reg_status",
    label: "Filing status",
    param: "reg_status",
    multiple: false,
    pinned: true,
    options: [
      { value: "active", label: "Active" },
      { value: "completed", label: "Completed" },
    ],
  },
];

interface RegulatoryFilingsSectionProps {
  companyId: string;
}

export default function RegulatoryFilingsSection({ companyId }: RegulatoryFilingsSectionProps) {
  const filterState = useLocalFilterState();
  const filters = useFilters(FILTER_SPECS, { state: filterState });
  const regStatus = filters.get("reg_status");
  const status = regStatus === "active" || regStatus === "completed" ? regStatus : "all";
  const keyword = filters.get("reg_kw") ?? "";

  const { data, isError, isLoading } = useQuery({
    queryKey: keys.masterCompany.regulatoryFilings(companyId, status, keyword),
    queryFn: () =>
      api.getCompanyRegulatoryFilings(companyId, {
        status,
        keyword: keyword || undefined,
        limit: 100,
      }),
    retry: false,
  });

  // Hide entirely when there is no data at all and no active filtering
  if (isError) return null;
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const isFiltered = filters.activeCount > 0;
  const showEmpty = !isLoading && items.length === 0;
  const shouldHide = !isLoading && items.length === 0 && !isFiltered;
  if (shouldHide) return null;

  return (
    <Panel
      title="Regulatory filings"
      count={total}
      subtitle={isFiltered ? "Matching your filters" : undefined}
      sx={{ mb: 3 }}
      action={
        <FilterBar filters={filters} variant="inline" />
      }
    >
      {showEmpty ? (
        <EmptyState title="No filings match your filters" />
      ) : (
        <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Title</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Filed</TableCell>
              <TableCell>Hearing</TableCell>
              <TableCell>MW</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((filing) => (
              <RegulatoryFilingRow key={filing.id} filing={filing} />
            ))}
          </TableBody>
        </ScrollTable>
      )}
    </Panel>
  );
}
